import { useNewIntegrationsStore } from "@/store/newIntegration"
import { FC, useEffect } from "react"
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom"

export type IntegrationStep =
  | "source-configuration"
  | "destination-configuration"
  | "enrichment-configuration"
  | "data-mapping"
  | "sync-schedule"
  | "master-entity"
  | "integration-profile"
  | "review"

export const isActiveStatus = (status?: string) => String(status || "").toLowerCase() === "active"

export function integrationPath(
  id: string | undefined,
  step?: IntegrationStep,
  extra?: { enrichmentTitle?: string; enrichmentId?: string; search?: string }
) {
  if (!id) return "/connector-library"
  const search = extra?.search || ""
  if (!step) return `/integration/${id}${search}`
  if (step === "enrichment-configuration") {
    const title = encodeURIComponent(extra?.enrichmentTitle || "enrichment")
    const enrichmentId = encodeURIComponent(extra?.enrichmentId || "new")
    return `/integration/${id}/enrichment-configuration/${title}/${enrichmentId}${search}`
  }
  return `/integration/${id}/${step}${search}`
}

const withLocation = (path: string, search: string, hash: string) => `${path}${search}${hash}`

/** Old start tree: /start-integration/:id/<step> */
export const RedirectStartStep: FC<{ step?: IntegrationStep }> = ({ step }) => {
  const { id, enrichmentTitle, enrichmentId } = useParams()
  const { search, hash } = useLocation()
  if (!id) return <Navigate to="/connector-library" replace />
  if (step === "enrichment-configuration") {
    return (
      <Navigate
        to={withLocation(
          `/integration/${id}/enrichment-configuration/${enrichmentTitle || "enrichment"}/${enrichmentId || "new"}`,
          search,
          hash
        )}
        replace
      />
    )
  }
  const target = step ? `/integration/${id}/${step}` : `/integration/${id}`
  return <Navigate to={withLocation(target, search, hash)} replace />
}

/** Old edit tree: /edit-integration/<step>/:id */
export const RedirectEditStep: FC<{ step?: IntegrationStep }> = ({ step }) => {
  const { id, enrichmentId, enrichmentTitle } = useParams()
  const { search, hash } = useLocation()
  if (!id) return <Navigate to="/connector-library" replace />
  if (step === "enrichment-configuration") {
    const title = enrichmentTitle || "enrichment"
    return (
      <Navigate
        to={withLocation(
          `/integration/${id}/enrichment-configuration/${title}/${enrichmentId || "new"}`,
          search,
          hash
        )}
        replace
      />
    )
  }
  const target = step ? `/integration/${id}/${step}` : `/integration/${id}`
  return <Navigate to={withLocation(target, search, hash)} replace />
}

/** Binds the integration store to the id in the URL before any step reads it. */
export const IntegrationScope: FC = () => {
  const { id } = useParams()
  const bindIntegration = useNewIntegrationsStore(state => state.bindIntegration)
  useEffect(() => {
    if (id) bindIntegration(id)
  }, [id, bindIntegration])
  return <Outlet />
}
